import { useCallback, useEffect, useMemo, useState } from "react";
import type { FilterItem, TagFilter } from "@/types/filters";

interface UseFilterModalOptions {
  open: boolean;
  initialFilters: FilterItem[];
  onApply: (filters: FilterItem[]) => void;
  onClose: () => void;
}

interface UseFilterModalReturn {
  /** Current local filter state (modified but not yet applied) */
  localFilters: FilterItem[];
  /** Update local filters directly */
  setLocalFilters: React.Dispatch<React.SetStateAction<FilterItem[]>>;
  /** Get array of selected values for a multi-select filter */
  getSelectedValues: (filterBy: string) => string[];
  /** Get single filter value (for inputs/selects) */
  getFilterValue: (filterBy: string, defaultValue?: string) => string;
  /** Set a single filter value (for inputs/selects) */
  setFilterValue: (filterBy: string, value: string) => void;
  /** Toggle a value in a multi-select filter */
  handleToggle: (filterBy: string, value: string) => void;
  /** Reset all filters to empty */
  handleReset: () => void;
  /** Apply current filters and close modal */
  handleApply: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Count of active filter values */
  activeFilterCount: number;
}

/**
 * Custom hook for managing filter modal state.
 * Eliminates duplicated filter logic across filter modal components.
 *
 * @example
 * ```tsx
 * function MyFilterModal({ open, onClose, onApply, filterValues }: Props) {
 *   const {
 *     getSelectedValues,
 *     handleToggle,
 *     handleReset,
 *     handleApply,
 *     hasActiveFilters,
 *     activeFilterCount,
 *   } = useFilterModal({ open, initialFilters: filterValues, onApply, onClose })
 *
 *   return (
 *     <ResponsiveModal
 *       open={open}
 *       onOpenChange={(isOpen) => !isOpen && onClose()}
 *       onReset={handleReset}
 *       resetDisabled={!hasActiveFilters}
 *       onConfirm={handleApply}
 *     >
 *       // Filter content...
 *     </ResponsiveModal>
 *   )
 * }
 * ```
 */
export function useFilterModal({
  open,
  initialFilters,
  onApply,
  onClose,
}: UseFilterModalOptions): UseFilterModalReturn {
  const [localFilters, setLocalFilters] =
    useState<FilterItem[]>(initialFilters);

  // Sync local filters when modal opens or initial filters change
  useEffect(() => {
    if (open) {
      setLocalFilters(initialFilters);
    }
  }, [open, initialFilters]);

  const getSelectedValues = useCallback(
    (filterBy: string): string[] => {
      const filter = localFilters.find((f) => f.filterBy === filterBy);
      if (!filter?.filter) return [];
      return Array.isArray(filter.filter)
        ? filter.filter
        : [filter.filter as string];
    },
    [localFilters]
  );

  const getFilterValue = useCallback(
    (filterBy: string, defaultValue = ""): string => {
      const item = localFilters.find((f) => f.filterBy === filterBy);
      if (!item?.filter) return defaultValue;
      return Array.isArray(item.filter)
        ? String(item.filter[0] ?? "")
        : String(item.filter);
    },
    [localFilters]
  );

  const setFilterValue = useCallback((filterBy: string, value: string) => {
    setLocalFilters((prev) => {
      if (!value) return prev.filter((filter) => filter.filterBy !== filterBy);
      const existingIndex = prev.findIndex(
        (filter) => filter.filterBy === filterBy
      );
      const newFilter: FilterItem = { filterBy, filter: value };
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newFilter;
        return updated;
      }
      return [...prev, newFilter];
    });
  }, []);

  const handleToggle = useCallback((filterBy: string, value: string) => {
    setLocalFilters((prev) => {
      const existingIndex = prev.findIndex((f) => f.filterBy === filterBy);
      const existing = existingIndex >= 0 ? prev[existingIndex] : null;
      const currentValues = existing?.filter
        ? Array.isArray(existing.filter)
          ? existing.filter
          : [existing.filter as string]
        : [];

      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      if (newValues.length === 0) {
        return prev.filter((f) => f.filterBy !== filterBy);
      }

      const newFilter: FilterItem = { filterBy, filter: newValues };
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newFilter;
        return updated;
      }
      return [...prev, newFilter];
    });
  }, []);

  const handleReset = useCallback(() => setLocalFilters([]), []);

  const handleApply = useCallback(() => {
    onApply(localFilters);
    onClose();
  }, [onApply, onClose, localFilters]);

  const hasActiveFilters = localFilters.length > 0;

  const activeFilterCount = useMemo(
    () =>
      localFilters.reduce((count, filter) => {
        const filterValue = filter.filter;
        if (Array.isArray(filterValue)) return count + filterValue.length;
        return count + 1;
      }, 0),
    [localFilters]
  );

  return {
    localFilters,
    setLocalFilters,
    getSelectedValues,
    getFilterValue,
    setFilterValue,
    handleToggle,
    handleReset,
    handleApply,
    hasActiveFilters,
    activeFilterCount,
  };
}

/**
 * Extended hook for filter modals that include tag filtering with AND/OR match modes.
 * Use this when you need tag filtering support in your filter modal.
 */
interface UseFilterModalWithTagsOptions extends UseFilterModalOptions {
  tagFilterBy: string;
}

interface UseFilterModalWithTagsReturn extends UseFilterModalReturn {
  /** Current tag match mode ('AND' or 'OR') */
  tagMatchMode: "AND" | "OR";
  /** Handler for changing tag match mode */
  handleTagMatchModeChange: (mode: "AND" | "OR") => void;
  /** Get selected tag IDs */
  getSelectedTags: () => string[];
  /** Toggle a tag in the filter */
  handleTagToggle: (tagId: string) => void;
  /** Reset handler that also resets tag match mode */
  handleReset: () => void;
}

export function useFilterModalWithTags({
  open,
  initialFilters,
  onApply,
  onClose,
  tagFilterBy,
}: UseFilterModalWithTagsOptions): UseFilterModalWithTagsReturn {
  const baseHook = useFilterModal({ open, initialFilters, onApply, onClose });
  const [tagMatchMode, setTagMatchMode] = useState<"AND" | "OR">("OR");

  // Sync tag match mode when modal opens
  useEffect(() => {
    if (!open) return;

    const tagFilter = initialFilters.find((f) => f.filterBy === tagFilterBy);
    if (
      tagFilter?.filter &&
      typeof tagFilter.filter === "object" &&
      !Array.isArray(tagFilter.filter)
    ) {
      const tf = tagFilter.filter as TagFilter;
      setTagMatchMode(tf.match || "OR");
    } else {
      setTagMatchMode("OR");
    }
  }, [initialFilters, open, tagFilterBy]);

  const getSelectedTags = useCallback((): string[] => {
    const filter = baseHook.localFilters.find(
      (f) => f.filterBy === tagFilterBy
    );
    if (!filter?.filter) return [];

    if (typeof filter.filter === "object" && !Array.isArray(filter.filter)) {
      return (filter.filter as TagFilter).ids || [];
    }

    return Array.isArray(filter.filter)
      ? filter.filter
      : [filter.filter as string];
  }, [baseHook.localFilters, tagFilterBy]);

  const handleTagToggle = useCallback(
    (tagId: string) => {
      baseHook.setLocalFilters((prev) => {
        const existingIndex = prev.findIndex((f) => f.filterBy === tagFilterBy);
        const existing = existingIndex >= 0 ? prev[existingIndex] : null;

        let currentIds: string[];
        if (
          existing?.filter &&
          typeof existing.filter === "object" &&
          !Array.isArray(existing.filter)
        ) {
          currentIds = (existing.filter as TagFilter).ids || [];
        } else if (existing?.filter) {
          currentIds = Array.isArray(existing.filter)
            ? existing.filter
            : [existing.filter as string];
        } else {
          currentIds = [];
        }

        const newIds = currentIds.includes(tagId)
          ? currentIds.filter((id) => id !== tagId)
          : [...currentIds, tagId];

        if (newIds.length === 0) {
          return prev.filter((f) => f.filterBy !== tagFilterBy);
        }

        const newFilter: FilterItem = {
          filterBy: tagFilterBy,
          filter: { ids: newIds, match: tagMatchMode } as TagFilter,
        };

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = newFilter;
          return updated;
        }
        return [...prev, newFilter];
      });
    },
    [tagFilterBy, tagMatchMode, baseHook.setLocalFilters]
  );

  const handleTagMatchModeChange = useCallback(
    (mode: "AND" | "OR") => {
      setTagMatchMode(mode);

      baseHook.setLocalFilters((prev) => {
        const existingIndex = prev.findIndex((f) => f.filterBy === tagFilterBy);
        if (existingIndex < 0) return prev;

        const existing = prev[existingIndex];
        let currentIds: string[] = [];
        if (
          existing.filter &&
          typeof existing.filter === "object" &&
          !Array.isArray(existing.filter)
        ) {
          currentIds = (existing.filter as TagFilter).ids || [];
        }

        if (currentIds.length === 0) return prev;

        const updated = [...prev];
        updated[existingIndex] = {
          filterBy: tagFilterBy,
          filter: { ids: currentIds, match: mode } as TagFilter,
        };
        return updated;
      });
    },
    [tagFilterBy, baseHook.setLocalFilters]
  );

  const handleReset = useCallback(() => {
    baseHook.setLocalFilters([]);
    setTagMatchMode("OR");
  }, [baseHook.setLocalFilters]);

  return {
    ...baseHook,
    tagMatchMode,
    handleTagMatchModeChange,
    getSelectedTags,
    handleTagToggle,
    handleReset,
  };
}
